import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Battery, Cable, Droplet, Sun, ArrowRight, Clock } from "lucide-react";

const CATS = [
  {
    id: "batteries", title: "Batteries", icon: Battery,
    desc: "Complete Amaron battery range for Car, Bike, Taxi, Truck, Tractor and Construction / SPV — for every vehicle and site need.",
    apps: ["Car & SUV", "Bike / Two-Wheeler", "Taxi / Three-Wheeler", "Truck & Commercial", "Tractor", "Construction / SPV"],
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/n7v5tmdk_batteries%26hups.JPG",
    findQuery: "batteries",
  },
  {
    id: "hups", title: "HUPS", icon: Cable,
    desc: "Amaron Pro Hi-Backup UPS units with pure sinewave output — HB, HBP and HE series (14 models, 850 VA to 6000 VA).",
    apps: ["HB Series (850–1850 VA)", "HBP Series (2500–6000 VA)", "HE Series (compact)", "Continuous duty"],
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/iefel4bi_hups1.png",
    findQuery: "hups",
  },
  {
    id: "lubricants", title: "Lubricants", icon: Droplet,
    desc: "Engine oils and lubricants engineered for petrol, diesel and two-wheeler engines — smoother, longer life.",
    apps: ["Petrol cars & SUVs", "Diesel cars & SUVs", "Two-wheelers", "Commercial vehicles"],
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/mxbzmm17_lubricant.webp",
    findQuery: "lubricants",
  },
  {
    id: "solar", title: "Solar Product Solution", icon: Sun,
    desc: "Solar batteries and solutions for homes, farms, shops and commercial rooftop setups.",
    apps: ["Home solar", "Farm / Pumps", "Shop backup", "Commercial rooftop"],
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/bq250s98_solar.webp",
    findQuery: "solar",
    comingSoon: true,
  },
];

export default function ProductsPage() {
  // Per-page canonical + og:url — the base tags in index.html point at the
  // homepage, so this route needs to override them or Google sees /products as
  // a duplicate of /.
  useEffect(() => {
    const canonicalHref = "https://amaronbatterystore.com/products";
    const restores = [];

    const setMeta = (name, content, attr = "property") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      const prev = el ? el.getAttribute("content") : null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      restores.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);
    restores.push(() => { if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical); });

    setMeta("og:url", canonicalHref);
    setMeta("og:title", "Products · Bharat Battery House — Amaron Sikar");
    setMeta("description", "Explore the complete Amaron product range at Bharat Battery House, Sikar — Batteries, HUPS, Lubricants and Solar. Genuine Amaron with warranty support.", "name");

    return () => restores.forEach((fn) => fn && fn());
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Products</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-2 tracking-tighter">Four product lines. One trusted brand.</h1>
        <p className="mt-4 text-muted-foreground">Explore our full Amaron product range and pick the right one with our team&apos;s help.</p>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {CATS.map(c => (
          <Card key={c.id} data-testid={`product-card-${c.id}`} className="bg-card border-black/10 rounded-2xl overflow-hidden group flex flex-col">
            <div className="relative aspect-[16/9]">
              <img src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="absolute top-4 left-4 h-10 w-10 rounded-lg bg-primary/90 border border-primary flex items-center justify-center">
                <c.icon className="h-5 w-5 text-primary-foreground" />
              </div>
              {c.comingSoon && (
                <Badge className="absolute top-4 right-4 bg-amber-500/95 text-black hover:bg-amber-500 border-0" data-testid={`product-coming-soon-${c.id}`}>
                  <Clock className="h-3 w-3 mr-1" /> Coming Soon
                </Badge>
              )}
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <h3 className="font-display text-2xl font-bold">{c.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{c.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {c.apps.map(a => (
                  <span key={a} className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-black/10 bg-black/5 text-foreground/80">{a}</span>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-black/10 flex flex-wrap gap-3">
                {c.comingSoon ? (
                  <Link to="/contact" data-testid={`product-notify-${c.id}`}>
                    <Button className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white">
                      Notify Me When Available <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/find-battery?category=${c.findQuery}`} data-testid={`product-find-${c.id}`}>
                    <Button className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white">
                      Find Product <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  </Link>
                )}
                <Link to="/contact" data-testid={`product-quote-${c.id}`}>
                  <Button variant="outline" className="rounded-full border-black/15">Request Quote</Button>
                </Link>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
